import { FormEvent, useState } from 'react'
import { Container } from '../components/Container'
import { apiClassification } from '../api'

type ClassificationResult = {
  ai?: string | number
  human?: string | number
  notes?: string
}

type Source = 'ai' | 'human' | 'unknown'

export function CallToAction() {
  const [text, setText] = useState('')
  const [result, setResult] = useState<ClassificationResult | null>(null)
  const [err, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [source, setSource] = useState<Source>('unknown')

  async function classify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError('')
    setResult(null)

    if (text.trim().length < 600) {
      setError('Please add at least 600 characters for a more useful analysis.')
      return
    }

    try {
      setLoading(true)
      const data = await apiClassification(text, 2, source, '')
      setResult(data as ClassificationResult)
    } catch {
      setError('Something went wrong while analysing the text. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section id="get-started-today" className="relative overflow-hidden bg-slate-950 py-24 sm:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.35),transparent_40%)]" />

      <Container className="relative">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-4xl font-semibold tracking-tight text-blue-400 sm:text-5xl">
            Test a sample of writing
          </h2>
          <p className="mt-4 text-lg text-slate-800">
            Paste text below and Digitalfalse will analyse whether it contains signals commonly associated with AI-generated writing.
          </p>
        </div>

        <form onSubmit={classify} className="mx-auto mt-10 max-w-3xl rounded-3xl bg-white p-4 shadow-2xl">
          <label htmlFor="text-sample" className="sr-only">
            Text sample
          </label>

          <textarea
            id="text-sample"
            rows={12}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
            placeholder="Paste at least 600 characters of text here..."
            required
          />

          <div className="mt-4 flex flex-col gap-4 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <fieldset className="flex items-center gap-4 text-sm text-slate-600">
              <span className="font-medium text-slate-900">Known source:</span>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="source"
                  value="ai"
                  checked={source === 'ai'}
                  onChange={(e) => setSource(e.target.value as Source)}
                />
                AI
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="source"
                  value="human"
                  checked={source === 'human'}
                  onChange={(e) => setSource(e.target.value as Source)}
                />
                Human
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="source"
                  value="unknown"
                  checked={source === 'unknown'}
                  onChange={(e) => setSource(e.target.value as Source)}
                />
                Unknown
              </label>
            </fieldset>

            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Analysing…' : 'Analyse text'}
            </button>
          </div>

          {err && (
            <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm font-medium text-red-700">
              {err}
            </div>
          )}

          {result?.notes && (
            <div className="mt-4 rounded-2xl bg-slate-50 p-5 text-left">
              <p className="text-sm font-semibold text-slate-900">Result notes</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">{result.notes}</p>

              {(result.ai || result.human) && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      AI score
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-slate-950">
                      {result.ai}
                    </p>
                  </div>

                  <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Human score
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-slate-950">
                      {result.human}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>

        <p className="mx-auto mt-6 max-w-3xl text-center text-sm leading-6 text-slate-400">
          AI detection is probabilistic, not proof. Treat results as a review signal rather than a final judgement.
        </p>
      </Container>
    </section>
  )
}